import { APIProvider } from '@vis.gl/react-google-maps'
import { t } from 'i18next'
import { useSearchParams } from 'react-router'

import { useSuspenseBlocos } from '@/features/blocos/hooks'
import { useSuspensePlantaDoLoteamento } from '@/features/empreendimentos/hooks/use-suspense-planta-do-loteamento'
import { useDetalhesLote } from '@/features/produtos/hooks/use-detalhes-lote'
import { useSuspenseLotesEmpreendimento } from '@/features/produtos/hooks/use-suspense-lotes-empreendimento'
import type { TProduto } from '@/features/produtos/interfaces/produto.types'
import { GoogleMapWithImageOverlay } from '@/pages/espelho-de-vendas-horizontal/map/google-map-with-image-overlay'
import { MenuNavegacaoQuadra } from '@/pages/espelho-de-vendas-horizontal/ui/menu-navegacao-quadra'
import { Render } from '@/shared/components/render-if'
import { Skeleton } from '@/shared/components/ui'
import { HeaderListaEspelhoDeVendas } from '@/shared/components/ui/header-lista-espelho-vendas'
import { type ILegendaItem } from '@/shared/components/ui/legendas-espelho-de-vendas'
import { SemCondicaoDePreco } from '@/shared/components/ui/sem-condicao-de-preco'
import type { Coordinate } from '@/shared/interfaces/mapa.types'

import { DetalhesLote } from './ui/detalhes-lote'

interface IListaQuadrasEspelhoDeVendasHorizontal {
  empreendimentoIdExterno: string
}

const ITENS_HORIZONTAL: ILegendaItem[] = [
  {
    variant: 'success',
    descricao: t('espelho-de-vendas:legendas.disponivel'),
    texto: t('espelho-de-vendas:legendas.andar'),
  },
  {
    variant: 'warning-dark',
    descricao: t('espelho-de-vendas:legendas.reservado'),
    texto: t('espelho-de-vendas:legendas.andar'),
  },
  {
    variant: 'warning',
    descricao: t('espelho-de-vendas:legendas.reservado'),
    texto: t('espelho-de-vendas:legendas.andar'),
  },
  {
    variant: 'destructive',
    descricao: t('espelho-de-vendas:legendas.indisponivel_reservado'),
    texto: t('espelho-de-vendas:legendas.andar'),
  },
]

const BASE_LATLNG_DELTA = 0.005
const GOOGLE_MAPS_API_KEY = import.meta.env.VENTAS_GOOGLE_MAPS_API_KEY
const INITIAL_REGION = {
  latitude: -23.300526602358175,
  longitude: -51.18327076600798,
  latitudeDelta: BASE_LATLNG_DELTA,
  longitudeDelta: BASE_LATLNG_DELTA,
}

export const ListaQuadrasEspelhoDeVendasHorizontal = ({
  empreendimentoIdExterno,
}: IListaQuadrasEspelhoDeVendasHorizontal) => {
  const { data: quadras } = useSuspenseBlocos(empreendimentoIdExterno)
  const { data: plantaDoLoteamento } = useSuspensePlantaDoLoteamento(empreendimentoIdExterno)
  const {
    data: quadrasComLotes,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useSuspenseLotesEmpreendimento(empreendimentoIdExterno)

  const [params, setSearchParams] = useSearchParams()

  const idExternoLoteSelecionado = Number(params.get('lote')) || 0
  const { data: detalhesLoteSelecionado, isFetching: isPendingDetalhesLote } = useDetalhesLote(idExternoLoteSelecionado)
  const isDetailsOpen = Boolean(idExternoLoteSelecionado)

  const calculatedOverlayCoordinates: [Coordinate, Coordinate] = [
    {
      latitude: Number.parseFloat(plantaDoLoteamento.limitesDaPlanta.norte),
      longitude: Number.parseFloat(plantaDoLoteamento.limitesDaPlanta.oeste),
    },
    {
      latitude: Number.parseFloat(plantaDoLoteamento.limitesDaPlanta.sul),
      longitude: Number.parseFloat(plantaDoLoteamento.limitesDaPlanta.leste),
    },
  ]

  const nenhumaQuadraComCondicaoDePreco = quadras
    .map((quadra) => quadra.condicaoDePrecoVigente)
    .every((condicao) => !condicao)

  const onLoteClick = (lote: TProduto) => {
    setSearchParams((state) => {
      state.set('lote', String(lote.idExterno))
      return state
    })
  }

  return (
    <div className="flex h-full flex-col">
      <Render.If condition={!nenhumaQuadraComCondicaoDePreco} fallback={<SemCondicaoDePreco />}>
        {() => (
          <>
            <HeaderListaEspelhoDeVendas
              refresh={refetch}
              titulo={t('espelho-de-vendas:header.selecione_lote')}
              isRefreshing={isFetching}
              dataUltimaAtualizacao={dataUpdatedAt}
              legendas={ITENS_HORIZONTAL}
            />
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <MenuNavegacaoQuadra quadras={quadras} initialRegion={INITIAL_REGION} />
              <div className="relative h-full">
                <GoogleMapWithImageOverlay
                  initialRegion={INITIAL_REGION}
                  baseLatLngDelta={BASE_LATLNG_DELTA}
                  quadras={quadras}
                  overlayCoordinates={calculatedOverlayCoordinates}
                  image={{
                    uri: plantaDoLoteamento.uri,
                  }}
                  lotes={quadrasComLotes.flatMap((quadra) => quadra.lotes)}
                  onLotePress={(lote) => {
                    const loteOriginal = quadrasComLotes.flatMap((q) => q.lotes).find((l) => l.id === lote.id)
                    if (loteOriginal) {
                      onLoteClick(loteOriginal as TProduto)
                    }
                  }}
                  className="h-full w-full"
                />
                <Render.If condition={isDetailsOpen}>
                  {() => (
                    <DetalhesLote
                      detalhesLote={detalhesLoteSelecionado}
                      isLoading={isPendingDetalhesLote}
                      idExternoEmpreendimento={empreendimentoIdExterno}
                    />
                  )}
                </Render.If>
              </div>
            </APIProvider>
          </>
        )}
      </Render.If>
    </div>
  )
}

export const ListaQuadrasEspelhoDeVendasHorizontalSkeleton = () => {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b p-4">
        <div className="space-y-2">
          <Skeleton className="h-5 w-32 animate-pulse rounded" />
          <Skeleton className="h-4 w-48 animate-pulse rounded" />
        </div>
        <Skeleton className="h-9 w-20 animate-pulse rounded" />
      </div>

      <div className="mb-8 flex-1">
        <Skeleton className="mb-4 h-10 w-40 animate-pulse rounded" />

        <div className="relative h-full">
          <Skeleton className="h-full w-full animate-pulse rounded border" />
        </div>
      </div>
    </div>
  )
}
